import { css } from '@emotion/react'
import React, { createRef, FC, useEffect } from 'react'
import { brandColors } from 'shared/basicPalette'
import { Box, Flex } from 'front-end/src/components'
import { fontSizes } from '../../chakraThemeExtension'

export interface IProgressRingProps {
  progress: number
  withPercentage?: boolean
}

const ProgressRing: FC<IProgressRingProps> = (props) => {
  const { progress, withPercentage = false } = props
  const ref = createRef<HTMLCanvasElement>()
  const sizeOfCircle = progress ? '64px' : '24px'

  useEffect(() => {
    draw(ref, props)
  }, [progress])

  return (
    <Flex
      w={sizeOfCircle}
      h={sizeOfCircle}
      position="relative"
      css={css`
        @keyframes rotating {
          from {
            -ms-transform: rotate(0deg);
            -moz-transform: rotate(0deg);
            -webkit-transform: rotate(0deg);
            -o-transform: rotate(0deg);
            transform: rotate(0deg);
          }
          to {
            -ms-transform: rotate(360deg);
            -moz-transform: rotate(360deg);
            -webkit-transform: rotate(360deg);
            -o-transform: rotate(360deg);
            transform: rotate(360deg);
          }
        }
        canvas {
          animation: rotating 2s linear infinite;
          width: ${sizeOfCircle};
          height: ${sizeOfCircle};
        }
      `}
    >
      <canvas width={sizeOfCircle} height={sizeOfCircle} ref={ref} />
      <Flex
        position="absolute"
        left="0"
        right="0"
        top="0"
        bottom="0"
        justifyContent="center"
        alignItems="center"
        color={getColorFromProgress(progress)}
        marginRight="-2px"
      >
        {typeof progress !== 'undefined' && withPercentage && (
          <Flex alignItems="baseline">
            <Box
              as="span"
              fontSize={
                Math.floor(progress / 100) ? fontSizes['2xl'] : fontSizes['3xl']
              }
            >
              {progress}
            </Box>
            <Box as="span" fontSize={fontSizes.xs}>
              %
            </Box>
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}
export default ProgressRing

const getColorFromProgress = (progress: number) => {
  return progress === 100 ? brandColors.green.mid : brandColors.blue.mid
}

const draw = (ref, props) => {
  const progress = props.progress / 100
  const canvas = ref.current
  const ctx = canvas?.getContext('2d')

  if (!ref || !ctx || !canvas) {
    return
  }

  const scale = window.devicePixelRatio
  const width = canvas.clientWidth * scale
  const height = canvas.clientHeight * scale
  const radius = width / 2
  const endAngle = 2 * progress * Math.PI
  const lineWidth = scale * 2
  const scaledLineWidth = lineWidth * scale
  const lineColor = getColorFromProgress(props.progress)
  const backgroundColor = brandColors.white
  const backgroundLineColor = brandColors.grey.lighter
  const SMALLEST_ALLOWABLE_ARC_RADIUS = 10
  ctx.lineWidth = scaledLineWidth

  canvas.width = width
  canvas.height = height

  ctx.clearRect(0, 0, canvas.clientHeight, canvas.clientWidth)
  ctx.lineWidth = scaledLineWidth

  function makeArc(arcRadius: number) {
    arcRadius = Math.max(SMALLEST_ALLOWABLE_ARC_RADIUS, arcRadius)
    ctx?.arc(radius, radius, arcRadius, 0, 2 * Math.PI)
  }

  ctx.beginPath()
  ctx.fillStyle = '#ffffff'
  makeArc(radius - scaledLineWidth - 1)
  ctx.fill()

  if (backgroundColor) {
    ctx.beginPath()
    ctx.fillStyle = backgroundColor
    makeArc(radius - scaledLineWidth - 1)
    ctx.fill()
  }

  if (backgroundLineColor) {
    ctx.beginPath()
    ctx.strokeStyle = backgroundLineColor
    makeArc(radius - scaledLineWidth / 2)
    ctx.stroke()
  }

  if (endAngle) {
    ctx.beginPath()
    ctx.arc(
      radius,
      radius,
      radius - scaledLineWidth / 2,
      -Math.PI / 2,
      endAngle - Math.PI / 2
    )

    ctx.strokeStyle = lineColor
    ctx.lineCap = 'round'
    ctx.stroke()
  }
}
